/**
 * ESG Supplier Performance Monitoring Service
 * 
 * Comprehensive supplier performance monitoring system following industry ESG standards:
 * - ISO 20400 (Sustainable Procurement)
 * - UN Global Compact (Supply Chain Sustainability)
 * - CDP Supply Chain Program
 * - EcoVadis ESG Performance Monitoring
 * - SASB (Sustainability Accounting Standards Board)
 * - GRI 308/414 (Supplier Environmental/Social Performance)
 * - ILO Conventions (Labor Performance Standards)
 * - OECD Guidelines for Performance Excellence
 * - Science Based Targets initiative (SBTi)
 * - TCFD (Task Force on Climate-related Financial Disclosures)
 * 
 * Features:
 * - Real-time performance tracking
 * - KPI dashboard and analytics
 * - ESG performance scoring
 * - Compliance monitoring
 * - Risk assessment integration
 * - Sustainability metrics
 * - Performance benchmarking
 * - Automated alerts and notifications
 * - Trend analysis and forecasting
 * - Performance improvement plans
 */

// Generate comprehensive supplier performance data
function generateSupplierPerformanceData() {
  // Performance categories and KPIs
  const performanceCategories = [
    'Environmental Impact',
    'Social Responsibility', 
    'Governance & Ethics',
    'Quality & Delivery',
    'Financial Performance',
    'Innovation & Technology',
    'Risk Management',
    'Sustainability Programs'
  ];

  // ESG frameworks for performance measurement
  const esgFrameworks = [
    'ISO 20400', 'UN Global Compact', 'CDP Supply Chain',
    'EcoVadis', 'SASB', 'GRI 308/414', 'ILO Conventions',
    'OECD Guidelines', 'SBTi', 'TCFD', 'UNGP', 'SA8000'
  ];

  // Generate supplier performance portfolio
  const supplierPerformance = [];
  for (let i = 1; i <= 247; i++) {
    const supplier = {
      id: `SUP-${String(i).padStart(4, '0')}`,
      name: generateSupplierName(),
      category: generateSupplierCategory(),
      region: generateRegion(),
      tier: generateTier(),
      relationship: generateRelationshipType(),
      contractValue: generateContractValue(),
      onboardingDate: generateOnboardingDate(),
      lastAssessment: generateRecentDate(),
      nextReview: generateFutureDate(),
      
      // Performance Scores
      overallScore: Math.round(Math.random() * 30 + 70), // 70-100 range
      environmentalScore: Math.round(Math.random() * 40 + 60), // 60-100 range
      socialScore: Math.round(Math.random() * 35 + 65), // 65-100 range
      governanceScore: Math.round(Math.random() * 25 + 75), // 75-100 range
      qualityScore: Math.round(Math.random() * 20 + 80), // 80-100 range
      deliveryScore: Math.round(Math.random() * 30 + 70), // 70-100 range
      
      // Performance Trends
      performanceTrend: generatePerformanceTrend(),
      riskLevel: generateRiskLevel(),
      complianceStatus: generateComplianceStatus(),
      certifications: generateCertifications(),
      
      // KPI Metrics
      kpis: generateKPIMetrics(),
      
      // Recent Performance Issues
      issues: generatePerformanceIssues(),
      
      // Improvement Plans
      improvementPlans: generateImprovementPlans(),
      
      // Performance History
      performanceHistory: generatePerformanceHistory(),
      
      // Sustainability Metrics
      sustainabilityMetrics: generateSustainabilityMetrics(),
      
      // Contact and Management
      accountManager: generateAccountManager(),
      lastContact: generateRecentDate(),
      status: generateSupplierStatus()
    };
    
    supplierPerformance.push(supplier);
  }

  // Generate performance analytics
  const performanceAnalytics = {
    overview: {
      totalSuppliers: supplierPerformance.length,
      averageScore: Math.round(supplierPerformance.reduce((sum, s) => sum + s.overallScore, 0) / supplierPerformance.length),
      topPerformers: supplierPerformance.filter(s => s.overallScore >= 90).length,
      underPerformers: supplierPerformance.filter(s => s.overallScore < 70).length,
      atRisk: supplierPerformance.filter(s => s.riskLevel === 'High' || s.riskLevel === 'Critical').length,
      compliant: supplierPerformance.filter(s => s.complianceStatus === 'Compliant').length
    },
    
    byCategory: performanceCategories.map(category => ({
      category,
      averageScore: Math.round(Math.random() * 20 + 75),
      trend: Math.random() > 0.5 ? 'improving' : Math.random() > 0.3 ? 'stable' : 'declining',
      suppliers: Math.floor(Math.random() * 50) + 30
    })),
    
    byRegion: ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'].map(region => ({
      region,
      suppliers: supplierPerformance.filter(s => s.region === region).length,
      averageScore: Math.round(Math.random() * 15 + 80),
      topPerformer: supplierPerformance.find(s => s.region === region && s.overallScore >= 90)?.name || 'N/A'
    })),
    
    trends: {
      lastQuarter: {
        scoreChange: (Math.random() - 0.5) * 10, // -5 to +5
        complianceChange: (Math.random() - 0.5) * 20, // -10 to +10
        riskChange: (Math.random() - 0.5) * 15 // -7.5 to +7.5
      },
      yearOverYear: {
        scoreImprovement: Math.round(Math.random() * 8 + 2), // 2-10%
        newCertifications: Math.floor(Math.random() * 15) + 5,
        riskReduction: Math.round(Math.random() * 12 + 3) // 3-15%
      }
    }
  };

  // Generate KPI dashboard data
  const kpiDashboard = {
    environmental: {
      carbonEmissions: {
        current: Math.round(Math.random() * 50000 + 100000), // tonnes CO2e
        target: Math.round(Math.random() * 40000 + 80000),
        reduction: Math.round(Math.random() * 15 + 5), // 5-20% reduction
        trend: 'decreasing'
      },
      wasteReduction: {
        current: Math.round(Math.random() * 20 + 70), // 70-90% waste diverted
        target: 95,
        improvement: Math.round(Math.random() * 8 + 2), // 2-10% improvement
        trend: 'improving'
      },
      renewableEnergy: {
        current: Math.round(Math.random() * 40 + 35), // 35-75% renewable
        target: 80,
        increase: Math.round(Math.random() * 12 + 3), // 3-15% increase
        trend: 'increasing'
      }
    },
    
    social: {
      laborCompliance: {
        current: Math.round(Math.random() * 10 + 88), // 88-98% compliant
        target: 100,
        violations: Math.floor(Math.random() * 5), // 0-4 violations
        trend: 'improving'
      },
      diversityInclusion: {
        current: Math.round(Math.random() * 20 + 65), // 65-85% diverse suppliers
        target: 90,
        improvement: Math.round(Math.random() * 5 + 2), // 2-7% improvement
        trend: 'improving'
      },
      communityImpact: {
        investmentMillion: Math.round(Math.random() * 8 + 2), // $2-10M investment
        beneficiaries: Math.floor(Math.random() * 50000 + 25000), // 25K-75K people
        projects: Math.floor(Math.random() * 30) + 15 // 15-45 projects
      }
    },
    
    governance: {
      ethicsCompliance: {
        current: Math.round(Math.random() * 8 + 92), // 92-100% compliant
        violations: Math.floor(Math.random() * 3), // 0-2 violations
        training: Math.round(Math.random() * 10 + 88), // 88-98% training completion
        trend: 'stable'
      },
      dataPrivacy: {
        breaches: Math.floor(Math.random() * 2), // 0-1 breaches
        certifications: Math.floor(Math.random() * 8) + 12, // 12-20 certifications
        compliance: Math.round(Math.random() * 5 + 95) // 95-100% compliant
      }
    },
    
    operational: {
      qualityMetrics: {
        defectRate: (Math.random() * 0.5 + 0.1).toFixed(2), // 0.1-0.6% defect rate
        onTimeDelivery: Math.round(Math.random() * 10 + 90), // 90-100%
        customerSatisfaction: (Math.random() * 0.5 + 4.5).toFixed(1) // 4.5-5.0/5
      },
      innovation: {
        patentsSubmitted: Math.floor(Math.random() * 15) + 5, // 5-20 patents
        processImprovements: Math.floor(Math.random() * 25) + 10, // 10-35 improvements
        digitalAdoption: Math.round(Math.random() * 30 + 60) // 60-90% digital adoption
      }
    }
  };

  // Generate compliance monitoring data
  const complianceMonitoring = {
    frameworks: esgFrameworks.map(framework => ({
      framework,
      status: Math.random() > 0.15 ? 'Compliant' : Math.random() > 0.5 ? 'In Progress' : 'Non-Compliant',
      lastAudit: generateRecentDate(),
      nextAudit: generateFutureDate(),
      score: Math.round(Math.random() * 25 + 75), // 75-100
      criticalIssues: Math.floor(Math.random() * 3), // 0-2 critical issues
      actions: Math.floor(Math.random() * 8) + 2, // 2-10 actions
      auditor: generateAuditor(),
      evidence: Math.floor(Math.random() * 20) + 15 // 15-35 evidence items
    })),
    
    riskAreas: [
      'Environmental Violations', 'Labor Rights', 'Data Security',
      'Financial Irregularities', 'Ethical Violations', 'Supply Chain Disruption'
    ].map(area => ({
      area,
      riskLevel: generateRiskLevel(),
      incidents: Math.floor(Math.random() * 4), // 0-3 incidents
      mitigation: generateMitigationPlan(),
      owner: generateRiskOwner(),
      lastReview: generateRecentDate()
    }))
  };

  // Generate performance alerts
  const performanceAlerts = [];
  for (let i = 0; i < 23; i++) {
    performanceAlerts.push({
      id: `ALERT-${String(i + 1).padStart(3, '0')}`,
      type: generateAlertType(),
      severity: generateAlertSeverity(),
      supplier: supplierPerformance[Math.floor(Math.random() * supplierPerformance.length)].name,
      supplierId: supplierPerformance[Math.floor(Math.random() * supplierPerformance.length)].id,
      message: generateAlertMessage(),
      timestamp: generateRecentTimestamp(),
      status: generateAlertStatus(),
      assignedTo: generateAccountManager(),
      category: performanceCategories[Math.floor(Math.random() * performanceCategories.length)]
    });
  }

  // Generate benchmarking data
  const benchmarkingData = {
    industryBenchmarks: performanceCategories.map(category => ({
      category,
      industryAverage: Math.round(Math.random() * 15 + 70), // 70-85
      topQuartile: Math.round(Math.random() * 10 + 88), // 88-98
      ourAverage: Math.round(Math.random() * 20 + 75), // 75-95
      gap: Math.round((Math.random() - 0.5) * 10), // -5 to +5
      ranking: Math.floor(Math.random() * 20) + 1 // 1-20 percentile
    })),
    
    competitorAnalysis: [
      'Industry Leader A', 'Industry Leader B', 'Competitor C', 'Competitor D'
    ].map(competitor => ({
      name: competitor,
      overallScore: Math.round(Math.random() * 20 + 75), // 75-95
      strengths: generateCompetitorStrengths(),
      gaps: generateCompetitorGaps(),
      marketShare: Math.round(Math.random() * 25 + 5) // 5-30%
    }))
  };

  // Generate improvement recommendations
  const improvementRecommendations = [
    {
      priority: 'High',
      category: 'Environmental',
      recommendation: 'Implement carbon reduction targets aligned with SBTi requirements',
      impact: 'Reduce supply chain emissions by 15-20%',
      timeline: '6-12 months',
      investment: '$250K - $500K',
      roi: '18-24 months'
    },
    {
      priority: 'High',
      category: 'Social',
      recommendation: 'Enhance supplier diversity program with targeted outreach',
      impact: 'Increase diverse supplier spend by 25%',
      timeline: '3-6 months',
      investment: '$100K - $200K',
      roi: '12-18 months'
    },
    {
      priority: 'Medium',
      category: 'Governance',
      recommendation: 'Implement automated ESG data collection platform',
      impact: 'Improve data accuracy by 30% and reduce manual effort',
      timeline: '4-8 months',
      investment: '$150K - $300K',
      roi: '15-20 months'
    },
    {
      priority: 'Medium',
      category: 'Quality',
      recommendation: 'Deploy predictive quality analytics across key suppliers',
      impact: 'Reduce defect rates by 40% and improve delivery times',
      timeline: '6-9 months',
      investment: '$200K - $400K',
      roi: '20-30 months'
    },
    {
      priority: 'Low',
      category: 'Innovation',
      recommendation: 'Establish supplier innovation collaboration program',
      impact: 'Accelerate new product development by 20%',
      timeline: '9-12 months',
      investment: '$300K - $600K',
      roi: '24-36 months'
    }
  ];

  return {
    supplierPerformance,
    performanceAnalytics,
    kpiDashboard,
    complianceMonitoring,
    performanceAlerts,
    benchmarkingData,
    improvementRecommendations,
    lastUpdated: new Date().toISOString()
  };
}

// Helper functions for data generation
function generateSupplierName() {
  const prefixes = ['Global', 'Advanced', 'Premier', 'Strategic', 'Innovative', 'Sustainable', 'Elite', 'Dynamic'];
  const types = ['Manufacturing', 'Technologies', 'Solutions', 'Industries', 'Systems', 'Enterprises', 'Group', 'Corp'];
  const specialties = ['Ltd', 'Inc', 'LLC', 'GmbH', 'SA', 'Pvt Ltd', 'Co', 'AG'];
  
  return `${prefixes[Math.floor(Math.random() * prefixes.length)]} ${types[Math.floor(Math.random() * types.length)]} ${specialties[Math.floor(Math.random() * specialties.length)]}`;
}

function generateSupplierCategory() {
  const categories = [
    'Raw Materials', 'Manufacturing', 'Technology', 'Logistics', 'Services',
    'Consulting', 'Energy', 'Construction', 'IT Services', 'Healthcare',
    'Financial Services', 'Automotive', 'Aerospace', 'Electronics', 'Textiles'
  ];
  return categories[Math.floor(Math.random() * categories.length)];
}

function generateRegion() {
  const regions = [
    'North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'
  ];
  return regions[Math.floor(Math.random() * regions.length)];
}

function generateTier() {
  const tiers = ['Tier 1', 'Tier 2', 'Tier 3'];
  const weights = [0.4, 0.35, 0.25]; // Distribution weights
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < tiers.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return tiers[i];
  }
  return 'Tier 2';
}

function generateRelationshipType() {
  const types = ['Strategic', 'Preferred', 'Standard', 'New', 'Under Review'];
  const weights = [0.15, 0.25, 0.4, 0.15, 0.05];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < types.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return types[i];
  }
  return 'Standard';
}

function generateContractValue() {
  const value = Math.floor(Math.random() * 50000000) + 500000; // $500K - $50M
  return `$${(value / 1000000).toFixed(1)}M`;
}

function generateOnboardingDate() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - Math.floor(Math.random() * 5) - 1); // 1-5 years ago
  date.setDate(date.getDate() - Math.floor(Math.random() * 365));
  return date.toISOString().split('T')[0];
}

function generateRecentDate() {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * 90));
  return date.toISOString().split('T')[0];
}

function generateFutureDate() {
  const date = new Date();
  date.setDate(date.getDate() + Math.floor(Math.random() * 180) + 30);
  return date.toISOString().split('T')[0];
}

function generateRecentTimestamp() {
  const date = new Date();
  date.setHours(date.getHours() - Math.floor(Math.random() * 168)); // Last week
  return date.toISOString();
}

function generatePerformanceTrend() {
  const trends = ['Improving', 'Stable', 'Declining'];
  const weights = [0.5, 0.35, 0.15];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < trends.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return trends[i];
  }
  return 'Stable';
}

function generateRiskLevel() {
  const levels = ['Low', 'Medium', 'High', 'Critical'];
  const weights = [0.45, 0.35, 0.15, 0.05];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < levels.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return levels[i];
  }
  return 'Medium';
}

function generateComplianceStatus() {
  const statuses = ['Compliant', 'Mostly Compliant', 'Partially Compliant', 'Non-Compliant'];
  const weights = [0.6, 0.25, 0.1, 0.05];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < statuses.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return statuses[i];
  }
  return 'Compliant';
}

function generateCertifications() {
  const allCertifications = [
    'ISO 9001', 'ISO 14001', 'ISO 45001', 'SA8000', 'Fair Trade',
    'FSC', 'LEED', 'Energy Star', 'B-Corp', 'Cradle to Cradle'
  ];
  
  const count = Math.floor(Math.random() * 4) + 1; // 1-4 certifications
  const selected = [];
  for (let i = 0; i < count; i++) {
    const cert = allCertifications[Math.floor(Math.random() * allCertifications.length)];
    if (!selected.includes(cert)) selected.push(cert);
  }
  
  return selected;
}

function generateKPIMetrics() {
  return {
    qualityDefects: (Math.random() * 0.8 + 0.1).toFixed(2) + '%',
    onTimeDelivery: Math.round(Math.random() * 15 + 85) + '%',
    costSavings: '$' + Math.floor(Math.random() * 500 + 50) + 'K',
    carbonReduction: Math.round(Math.random() * 20 + 5) + '%',
    wasteReduction: Math.round(Math.random() * 25 + 10) + '%',
    energyEfficiency: Math.round(Math.random() * 15 + 10) + '%',
    diversitySpend: Math.round(Math.random() * 30 + 15) + '%',
    innovationIndex: (Math.random() * 2 + 3).toFixed(1) + '/5'
  };
}

function generatePerformanceIssues() {
  const issueTypes = [
    'Quality concerns', 'Delivery delays', 'ESG non-compliance',
    'Cost overruns', 'Communication gaps', 'Documentation issues'
  ];
  
  const count = Math.floor(Math.random() * 3); // 0-2 issues
  const issues = [];
  
  for (let i = 0; i < count; i++) {
    issues.push({
      type: issueTypes[Math.floor(Math.random() * issueTypes.length)],
      severity: generateRiskLevel(),
      date: generateRecentDate(),
      status: Math.random() > 0.3 ? 'Resolved' : 'Open'
    });
  }
  
  return issues;
}

function generateImprovementPlans() {
  const plans = [
    'ESG compliance enhancement', 'Quality improvement program',
    'Delivery optimization', 'Cost reduction initiative',
    'Innovation collaboration', 'Digital transformation'
  ];
  
  const count = Math.floor(Math.random() * 2) + 1; // 1-2 plans
  const selected = [];
  
  for (let i = 0; i < count; i++) {
    const plan = plans[Math.floor(Math.random() * plans.length)];
    if (!selected.find(p => p.plan === plan)) {
      selected.push({
        plan,
        startDate: generateRecentDate(),
        targetDate: generateFutureDate(),
        progress: Math.round(Math.random() * 80 + 10), // 10-90%
        status: Math.random() > 0.2 ? 'On Track' : 'At Risk'
      });
    }
  }
  
  return selected;
}

function generatePerformanceHistory() {
  const history = [];
  for (let i = 11; i >= 0; i--) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    
    history.push({
      month: date.toISOString().substr(0, 7), // YYYY-MM format
      overallScore: Math.round(Math.random() * 20 + 70 + (i * 0.5)), // Slight upward trend
      environmentalScore: Math.round(Math.random() * 25 + 65 + (i * 0.3)),
      socialScore: Math.round(Math.random() * 20 + 70 + (i * 0.4)),
      governanceScore: Math.round(Math.random() * 15 + 75 + (i * 0.2))
    });
  }
  
  return history;
}

function generateSustainabilityMetrics() {
  return {
    carbonEmissions: Math.round(Math.random() * 10000 + 5000) + ' tCO2e',
    waterUsage: Math.round(Math.random() * 50000 + 25000) + ' m³',
    wasteGenerated: Math.round(Math.random() * 500 + 100) + ' tonnes',
    renewableEnergy: Math.round(Math.random() * 60 + 20) + '%',
    recyclingRate: Math.round(Math.random() * 40 + 50) + '%',
    biodiversityProjects: Math.floor(Math.random() * 8) + 2,
    communityInvestment: '$' + Math.floor(Math.random() * 500 + 100) + 'K',
    localProcurement: Math.round(Math.random() * 50 + 30) + '%'
  };
}

function generateAccountManager() {
  const managers = [
    'Sarah Chen', 'Michael Rodriguez', 'Emily Johnson', 'David Park',
    'Lisa Thompson', 'James Wilson', 'Anna Kumar', 'Robert Martinez',
    'Jennifer Lee', 'Christopher Brown', 'Amanda Singh', 'Matthew Davis'
  ];
  return managers[Math.floor(Math.random() * managers.length)];
}

function generateSupplierStatus() {
  const statuses = ['Active', 'Under Review', 'Probation', 'Suspended'];
  const weights = [0.8, 0.1, 0.07, 0.03];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < statuses.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return statuses[i];
  }
  return 'Active';
}

function generateAuditor() {
  const auditors = [
    'Ernst & Young', 'Deloitte', 'PwC', 'KPMG', 'BDO', 'Grant Thornton',
    'Internal Audit Team', 'Third-Party ESG Assessor', 'Compliance Partners'
  ];
  return auditors[Math.floor(Math.random() * auditors.length)];
}

function generateMitigationPlan() {
  const plans = [
    'Enhanced monitoring protocols', 'Additional training programs',
    'Third-party verification', 'Process improvement initiatives',
    'Regular audit schedule', 'Stakeholder engagement'
  ];
  return plans[Math.floor(Math.random() * plans.length)];
}

function generateRiskOwner() {
  const owners = [
    'Risk Management Team', 'ESG Committee', 'Procurement Director',
    'Compliance Officer', 'Sustainability Manager', 'Operations Director'
  ];
  return owners[Math.floor(Math.random() * owners.length)];
}

function generateAlertType() {
  const types = [
    'Performance Decline', 'Compliance Issue', 'Quality Alert',
    'Delivery Risk', 'ESG Violation', 'Cost Variance',
    'Risk Assessment', 'Certification Expiry'
  ];
  return types[Math.floor(Math.random() * types.length)];
}

function generateAlertSeverity() {
  const severities = ['Low', 'Medium', 'High', 'Critical'];
  const weights = [0.3, 0.4, 0.25, 0.05];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < severities.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return severities[i];
  }
  return 'Medium';
}

function generateAlertMessage() {
  const messages = [
    'ESG score dropped below threshold',
    'Quality metrics showing declining trend',
    'Delivery performance below SLA requirements',
    'Compliance certification approaching expiry',
    'Environmental violation reported',
    'Cost variance exceeding agreed limits',
    'Risk assessment requires immediate attention',
    'Supplier relationship status needs review'
  ];
  return messages[Math.floor(Math.random() * messages.length)];
}

function generateAlertStatus() {
  const statuses = ['Open', 'In Progress', 'Resolved', 'Escalated'];
  const weights = [0.3, 0.4, 0.25, 0.05];
  const random = Math.random();
  let cumulative = 0;
  
  for (let i = 0; i < statuses.length; i++) {
    cumulative += weights[i];
    if (random <= cumulative) return statuses[i];
  }
  return 'Open';
}

function generateCompetitorStrengths() {
  const strengths = [
    'Advanced ESG programs', 'Strong quality management',
    'Digital transformation', 'Innovation partnerships',
    'Cost optimization', 'Risk management excellence'
  ];
  const count = Math.floor(Math.random() * 3) + 1;
  return strengths.slice(0, count);
}

function generateCompetitorGaps() {
  const gaps = [
    'Limited transparency', 'Weak sustainability metrics',
    'Poor supplier diversity', 'Inadequate risk monitoring',
    'Slow innovation adoption', 'High operational costs'
  ];
  const count = Math.floor(Math.random() * 2) + 1;
  return gaps.slice(0, count);
}

// Service state management
let performanceData = null;
let subscribers = new Set();

// Initialize data
function initializePerformanceData() {
  if (!performanceData) {
    performanceData = generateSupplierPerformanceData();
  }
  return performanceData;
}

// Subscription management
export function subscribeToPerformanceData(callback) {
  subscribers.add(callback);
  callback(initializePerformanceData());
  
  return () => {
    subscribers.delete(callback);
  };
}

function notifySubscribers() {
  subscribers.forEach(callback => callback(performanceData));
}

// Data access functions
export function getSupplierPerformance() {
  return initializePerformanceData().supplierPerformance;
}

export function getPerformanceAnalytics() {
  return initializePerformanceData().performanceAnalytics;
}

export function getKPIDashboard() {
  return initializePerformanceData().kpiDashboard;
}

export function getComplianceMonitoring() {
  return initializePerformanceData().complianceMonitoring;
}

export function getPerformanceAlerts() {
  return initializePerformanceData().performanceAlerts;
}

export function getBenchmarkingData() {
  return initializePerformanceData().benchmarkingData;
}

export function getImprovementRecommendations() {
  return initializePerformanceData().improvementRecommendations;
}

// Performance management functions
export function updateSupplierScore(supplierId, category, newScore) {
  const data = initializePerformanceData();
  const supplier = data.supplierPerformance.find(s => s.id === supplierId);
  
  if (supplier) {
    supplier[`${category}Score`] = newScore;
    
    // Recalculate overall score
    const scores = [
      supplier.environmentalScore,
      supplier.socialScore,
      supplier.governanceScore,
      supplier.qualityScore,
      supplier.deliveryScore
    ];
    supplier.overallScore = Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
    
    notifySubscribers();
  }
}

export function addPerformanceAlert(alert) {
  const data = initializePerformanceData();
  data.performanceAlerts.unshift({
    ...alert,
    id: `ALERT-${String(data.performanceAlerts.length + 1).padStart(3, '0')}`,
    timestamp: new Date().toISOString(),
    status: 'Open'
  });
  notifySubscribers();
}

export function updateAlertStatus(alertId, newStatus) {
  const data = initializePerformanceData();
  const alert = data.performanceAlerts.find(a => a.id === alertId);
  
  if (alert) {
    alert.status = newStatus;
    notifySubscribers();
  }
}

export function assignAlert(alertId, assignee) {
  const data = initializePerformanceData();
  const alert = data.performanceAlerts.find(a => a.id === alertId);
  
  if (alert) {
    alert.assignedTo = assignee;
    notifySubscribers();
  }
}

// Analytics functions
export function getPerformanceTrends(supplierId, timeframe = '12m') {
  const data = initializePerformanceData();
  const supplier = data.supplierPerformance.find(s => s.id === supplierId);
  
  if (supplier) {
    return supplier.performanceHistory;
  }
  return [];
}

export function getSupplierBenchmark(supplierId) {
  const data = initializePerformanceData();
  const supplier = data.supplierPerformance.find(s => s.id === supplierId);
  
  if (supplier) {
    const categoryAverage = data.performanceAnalytics.byCategory.find(c => 
      c.category.toLowerCase().includes(supplier.category.toLowerCase())
    );
    
    return {
      supplier: supplier.overallScore,
      categoryAverage: categoryAverage?.averageScore || 80,
      industryAverage: 78,
      topQuartile: 90
    };
  }
  return null;
}

// Reporting functions
export function generatePerformanceReport(filters = {}) {
  const data = initializePerformanceData();
  
  let filteredSuppliers = data.supplierPerformance;
  
  if (filters.region) {
    filteredSuppliers = filteredSuppliers.filter(s => s.region === filters.region);
  }
  
  if (filters.category) {
    filteredSuppliers = filteredSuppliers.filter(s => s.category === filters.category);
  }
  
  if (filters.tier) {
    filteredSuppliers = filteredSuppliers.filter(s => s.tier === filters.tier);
  }
  
  return {
    suppliers: filteredSuppliers,
    summary: {
      total: filteredSuppliers.length,
      averageScore: Math.round(filteredSuppliers.reduce((sum, s) => sum + s.overallScore, 0) / filteredSuppliers.length),
      topPerformers: filteredSuppliers.filter(s => s.overallScore >= 90).length,
      underPerformers: filteredSuppliers.filter(s => s.overallScore < 70).length
    },
    generatedAt: new Date().toISOString()
  };
}

export function exportPerformanceData(format = 'json') {
  const data = initializePerformanceData();
  
  const exportData = {
    generatedAt: new Date().toISOString(),
    format,
    summary: data.performanceAnalytics.overview,
    suppliers: data.supplierPerformance,
    kpis: data.kpiDashboard,
    compliance: data.complianceMonitoring,
    alerts: data.performanceAlerts,
    benchmarks: data.benchmarkingData,
    recommendations: data.improvementRecommendations
  };
  
  return exportData;
}

// Default export with main functions
export default {
  subscribeToPerformanceData,
  getSupplierPerformance,
  getPerformanceAnalytics,
  getKPIDashboard,
  getComplianceMonitoring,
  getPerformanceAlerts,
  getBenchmarkingData,
  getImprovementRecommendations,
  updateSupplierScore,
  addPerformanceAlert,
  updateAlertStatus,
  assignAlert,
  getPerformanceTrends,
  getSupplierBenchmark,
  generatePerformanceReport,
  exportPerformanceData
};